import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Table, Button, Form, Badge, Alert, Spinner, Modal, ButtonGroup } from 'react-bootstrap';
import { FaFileAlt, FaSearch, FaEye, FaUser, FaCar, FaCalendarAlt, FaMoneyBillWave, FaBuilding, FaDownload, FaFilter, FaCloudUploadAlt, FaFile } from 'react-icons/fa';
import api from '../services/api';

const AdminApplicationsPage = () => {
    const [applications, setApplications] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statistics, setStatistics] = useState(null);
    const [error, setError] = useState('');

    // Modal states
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [selectedApplication, setSelectedApplication] = useState(null);

    const [applicationDocuments, setApplicationDocuments] = useState([]);
    const [documentsLoading, setDocumentsLoading] = useState(false);

    // Filter states
    const [filters, setFilters] = useState({
        search: '',
        status: 'all',
        dealerFilter: 'all',
        dateFrom: '',
        dateTo: ''
    });

    const [pagination, setPagination] = useState({
        page: 1,
        limit: 20,
        total: 0,
        pages: 0
    });

    useEffect(() => {
        fetchApplications();
    }, [filters, pagination.page]);

    const fetchApplications = async () => {
        try {
            setLoading(true);
            const response = await api.get('/applications/admin', {
                params: {
                    ...filters,
                    page: pagination.page,
                    limit: pagination.limit
                }
            });

            if (response.data.success) {
                setApplications(response.data.applications || []);
                setStatistics(response.data.statistics);

                if (response.data.pagination) {
                    setPagination(response.data.pagination);
                }
            }
        } catch (error) {
            console.error('Başvurular yüklenirken hata:', error);
            setError('Başvurular yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            pending: { variant: 'warning', text: 'Beklemede' },
            approved: { variant: 'success', text: 'Onaylandı' },
            rejected: { variant: 'danger', text: 'Reddedildi' },
            completed: { variant: 'primary', text: 'Tamamlandı' }
        };

        const config = statusConfig[status] || { variant: 'secondary', text: status };
        return <Badge bg={config.variant}>{config.text}</Badge>;
    };

    const getDealerBadge = (application) => {
        if (application.dealerInfo && application.dealerInfo.isSubmittedByDealer) {
            return (
                <Badge bg="info" className="me-1">
                    <FaBuilding className="me-1" />
                    {application.dealerInfo.dealerName}
                </Badge>
            );
        }
        return (
            <Badge bg="secondary" className="me-1">
                <FaUser className="me-1" />
                Bireysel
            </Badge>
        );
    };

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0
        }).format(amount);
    };

    const formatDate = (date) => {
        return new Date(date).toLocaleDateString('tr-TR');
    };

    const handleViewDetails = async (application) => {
        setSelectedApplication(application);
        setApplicationDocuments([]);
        setShowDetailModal(true);

        // Belgeleri getir
        try {
            setDocumentsLoading(true);
            const response = await api.get(`/documents/application/${application._id}`);
            if (response.data.success) {
                setApplicationDocuments(response.data.documents);
            }
        } catch (error) {
            console.error('Belgeler yüklenirken hata:', error);
        } finally {
            setDocumentsLoading(false);
        }
    };
    const handleDownload = async (document) => {
        try {
            const response = await fetch(document.file.cloudinaryUrl);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = window.document.createElement('a');
            a.href = url;
            a.download = document.file.originalName || 'belge';
            window.document.body.appendChild(a);
            a.click();
            window.document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download error:', error);
        }
    };
    const clearFilters = () => {
        setFilters({
            search: '',
            status: 'all',
            dealerFilter: 'all',
            dateFrom: '',
            dateTo: ''
        });
        setPagination({ ...pagination, page: 1 });
    };

    const exportApplications = () => {
        // TODO: Export functionality
        alert('Export özelliği yakında eklenecek!');
    };

    return (
        <Container className="py-5">
            <Row className="mb-4">
                <Col>
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h1>
                                <FaFileAlt className="me-2" />
                                Başvuru Yönetimi
                            </h1>
                            <p className="text-muted">
                                Tüm müşteri başvurularını görüntüleyin ve yönetin
                            </p>
                        </div>
                        <Button variant="outline-success" onClick={exportApplications}>
                            <FaDownload className="me-2" />
                            Excel'e Aktar
                        </Button>
                    </div>
                </Col>
            </Row>

            {/* Error Alert */}
            {error && (
                <Alert variant="danger" dismissible onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            {/* Başvuru Listesi */}
            <Card>
                <Card.Header>
                    <div className="d-flex justify-content-between align-items-center">
                        <h5 className="mb-0">
                            <FaFileAlt className="me-2" />
                            Tüm Başvurular ({statistics?.total || 0})
                        </h5>
                        <ButtonGroup size="sm">
                            <Button variant="outline-primary" onClick={() => setPagination({ ...pagination, limit: 10 })}>
                                10
                            </Button>
                            <Button variant="outline-primary" onClick={() => setPagination({ ...pagination, limit: 20 })}>
                                20
                            </Button>
                            <Button variant="outline-primary" onClick={() => setPagination({ ...pagination, limit: 50 })}>
                                50
                            </Button>
                        </ButtonGroup>
                    </div>
                </Card.Header>
                <Card.Body className="p-0">
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" />
                            <p className="mt-2">Başvurular yükleniyor...</p>
                        </div>
                    ) : applications.length === 0 ? (
                        <div className="text-center py-5">
                            <FaFileAlt size={50} className="text-muted mb-3" />
                            <h5 className="text-muted">Başvuru bulunamadı</h5>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <Table hover className="mb-0">
                                <thead className="table-dark">
                                    <tr>
                                        <th>
                                            <FaUser className="me-1" />
                                            Müşteri
                                        </th>
                                        <th>Başvuru Türü</th>
                                        <th>
                                            <FaCar className="me-1" />
                                            Araç Bilgisi
                                        </th>
                                        <th>
                                            <FaMoneyBillWave className="me-1" />
                                            Finansman
                                        </th>
                                        <th>Durum</th>
                                        <th>
                                            <FaCalendarAlt className="me-1" />
                                            Tarih
                                        </th>
                                        <th>İşlemler</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {applications.map(application => (
                                        <tr key={application._id}>
                                            <td>
                                                <div>
                                                    <strong>{application.name} {application.surname}</strong>
                                                    <br />
                                                    <small className="text-muted">
                                                        TC: {application.tcNumber}
                                                    </small>
                                                    <br />
                                                    <small className="text-muted">
                                                        Tel: {application.phone}
                                                    </small>
                                                </div>
                                            </td>
                                            <td>
                                                {getDealerBadge(application)}
                                            </td>
                                            <td>
                                                <div>
                                                    <strong>
                                                        {application.preferredVehicleBrand || 'Belirtilmemiş'} {' '}
                                                        {application.preferredVehicleModel || ''}
                                                    </strong>
                                                    <br />
                                                    <small className="text-muted">
                                                        Yıl: {application.vehicleYear || 'Belirtilmemiş'}
                                                    </small>
                                                    {application.downPayment > 0 && (
                                                        <>
                                                            <br />
                                                            <small className="text-success">
                                                                Peşinat: {formatCurrency(application.downPayment)}
                                                            </small>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                            <td>
                                                <div>
                                                    <strong className="text-primary">
                                                        {formatCurrency(application.vehicleAmount)}
                                                    </strong>
                                                    <br />
                                                    <small className="text-muted">
                                                        {formatCurrency(application.monthlyPayment)}/ay
                                                    </small>
                                                    <br />
                                                    <small className="text-muted">
                                                        {application.term} ay vade
                                                    </small>
                                                </div>
                                            </td>
                                            <td>
                                                {getStatusBadge(application.status)}
                                            </td>
                                            <td>
                                                <small>{formatDate(application.createdAt)}</small>
                                            </td>
                                            <td>
                                                <Button
                                                    variant="outline-primary"
                                                    size="sm"
                                                    title="Detayları Görüntüle"
                                                    onClick={() => handleViewDetails(application)}
                                                >
                                                    <FaEye />
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                    )}
                </Card.Body>
            </Card>

            {/* Pagination */}
            {pagination.pages > 1 && (
                <div className="d-flex justify-content-center mt-4">
                    <Button
                        variant="outline-primary"
                        disabled={pagination.page <= 1}
                        onClick={() => setPagination({ ...pagination, page: pagination.page - 1 })}
                        className="me-2"
                    >
                        Önceki
                    </Button>
                    <span className="align-self-center mx-3">
                        Sayfa {pagination.page} / {pagination.pages}
                    </span>
                    <Button
                        variant="outline-primary"
                        disabled={pagination.page >= pagination.pages}
                        onClick={() => setPagination({ ...pagination, page: pagination.page + 1 })}
                    >
                        Sonraki
                    </Button>
                </div>
            )}

            {/* Özet İstatistikler */}
            {applications.length > 0 && (
                <Card className="mt-4 bg-light">
                    <Card.Body>
                        <Row className="text-center">
                            <Col md={2}>
                                <h6 className="text-muted mb-1">Bu Sayfa</h6>
                                <h4 className="text-primary">{applications.length}</h4>
                                <small className="text-muted">Başvuru</small>
                            </Col>
                            <Col md={2}>
                                <h6 className="text-muted mb-1">Toplam Tutar</h6>
                                <h4 className="text-success">
                                    {formatCurrency(
                                        applications.reduce((sum, app) => sum + app.vehicleAmount, 0)
                                    )}
                                </h4>
                                <small className="text-muted">Araç Bedeli</small>
                            </Col>
                            <Col md={2}>
                                <h6 className="text-muted mb-1">Ortalama Vade</h6>
                                <h4 className="text-info">
                                    {applications.length > 0 ? Math.round(
                                        applications.reduce((sum, app) => sum + app.term, 0) / applications.length
                                    ) : 0}
                                </h4>
                                <small className="text-muted">Ay</small>
                            </Col>
                            <Col md={2}>
                                <h6 className="text-muted mb-1">Bayi Başvuruları</h6>
                                <h4 className="text-warning">
                                    {applications.filter(app => app.dealerInfo?.isSubmittedByDealer).length}
                                </h4>
                                <small className="text-muted">Adet</small>
                            </Col>
                            <Col md={2}>
                                <h6 className="text-muted mb-1">Bireysel</h6>
                                <h4 className="text-secondary">
                                    {applications.filter(app => !app.dealerInfo?.isSubmittedByDealer).length}
                                </h4>
                                <small className="text-muted">Adet</small>
                            </Col>
                            <Col md={2}>
                                <h6 className="text-muted mb-1">Başarı Oranı</h6>
                                <h4 className="text-success">
                                    {statistics ? Math.round(
                                        ((statistics.byStatus.approved || 0) + (statistics.byStatus.completed || 0)) /
                                        (statistics.total || 1) * 100
                                    ) : 0}%
                                </h4>
                                <small className="text-muted">Onay</small>
                            </Col>
                        </Row>
                    </Card.Body>
                </Card>
            )}

            {/* Başvuru Detay Modal */}
            <Modal show={showDetailModal} onHide={() => setShowDetailModal(false)} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>
                        <FaFileAlt className="me-2" />
                        Başvuru Detayları
                        {selectedApplication?.dealerInfo?.isSubmittedByDealer && (
                            <Badge bg="info" className="ms-2">
                                <FaBuilding className="me-1" />
                                {selectedApplication.dealerInfo.dealerName}
                            </Badge>
                        )}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {selectedApplication && (
                        <Row>
                            <Col md={6}>
                                <Card className="mb-3">
                                    <Card.Header>
                                        <h6 className="mb-0">
                                            <FaUser className="me-2" />
                                            Müşteri Bilgileri
                                        </h6>
                                    </Card.Header>
                                    <Card.Body>
                                        <p><strong>Ad Soyad:</strong> {selectedApplication.name} {selectedApplication.surname}</p>
                                        <p><strong>TC Kimlik:</strong> {selectedApplication.tcNumber}</p>
                                        <p><strong>Telefon:</strong> {selectedApplication.phone}</p>
                                        <p><strong>Email:</strong> {selectedApplication.email}</p>
                                        <p><strong>Doğum Tarihi:</strong> {formatDate(selectedApplication.birthDate)}</p>
                                        <p><strong>Adres:</strong> {selectedApplication.address}, {selectedApplication.district}, {selectedApplication.city}</p>
                                        <p><strong>Eğitim:</strong> {selectedApplication.education}</p>
                                    </Card.Body>
                                </Card>

                                <Card className="mb-3">
                                    <Card.Header>
                                        <h6 className="mb-0">İş Bilgileri</h6>
                                    </Card.Header>
                                    <Card.Body>
                                        <p><strong>Meslek:</strong> {selectedApplication.occupation || 'Belirtilmemiş'}</p>
                                        <p><strong>Çalışma Şekli:</strong> {selectedApplication.employmentType || 'Belirtilmemiş'}</p>
                                        <p><strong>İşyeri:</strong> {selectedApplication.workplaceName || 'Belirtilmemiş'}</p>
                                        <p><strong>Çalışma Süresi:</strong> {selectedApplication.employmentDuration || 'Belirtilmemiş'}</p>
                                        <p><strong>Aylık Gelir:</strong> {formatCurrency(selectedApplication.income)}</p>
                                    </Card.Body>
                                </Card>
                            </Col>

                            <Col md={6}>
                                <Card className="mb-3">
                                    <Card.Header>
                                        <h6 className="mb-0">
                                            <FaCar className="me-2" />
                                            Araç Bilgileri
                                        </h6>
                                    </Card.Header>
                                    <Card.Body>
                                        <p><strong>Marka:</strong> {selectedApplication.preferredVehicleBrand || 'Belirtilmemiş'}</p>
                                        <p><strong>Model:</strong> {selectedApplication.preferredVehicleModel || 'Belirtilmemiş'}</p>
                                        <p><strong>Yıl:</strong> {selectedApplication.vehicleYear || 'Belirtilmemiş'}</p>
                                        {selectedApplication.notes && (
                                            <p><strong>Notlar:</strong> {selectedApplication.notes}</p>
                                        )}
                                    </Card.Body>
                                </Card>

                                <Card className="mb-3">
                                    <Card.Header>
                                        <h6 className="mb-0">
                                            <FaMoneyBillWave className="me-2" />
                                            Finansman Detayları
                                        </h6>
                                    </Card.Header>
                                    <Card.Body>
                                        <p><strong>Araç Bedeli:</strong> {formatCurrency(selectedApplication.vehicleAmount)}</p>
                                        <p><strong>Peşinat:</strong> {formatCurrency(selectedApplication.downPayment || 0)}</p>
                                        <p><strong>Aylık Ödeme:</strong> {formatCurrency(selectedApplication.monthlyPayment)}</p>
                                        <p><strong>Vade:</strong> {selectedApplication.term} ay</p>
                                        <p><strong>Toplam Ödeme:</strong> {formatCurrency(selectedApplication.monthlyPayment * selectedApplication.term)}</p>
                                        <p><strong>Durum:</strong> {getStatusBadge(selectedApplication.status)}</p>
                                    </Card.Body>
                                </Card>

                                <Card>
                                    <Card.Header>
                                        <h6 className="mb-0">
                                            <FaCalendarAlt className="me-2" />
                                            Başvuru Bilgileri
                                        </h6>
                                    </Card.Header>
                                    <Card.Body>
                                        <p><strong>Başvuru Tarihi:</strong> {formatDate(selectedApplication.createdAt)}</p>
                                        <p><strong>Son Güncelleme:</strong> {formatDate(selectedApplication.updatedAt)}</p>
                                        <p><strong>Başvuru ID:</strong> {selectedApplication._id}</p>
                                        {selectedApplication.dealerInfo?.isSubmittedByDealer && (
                                            <div className="mt-3 p-2 bg-info bg-opacity-10 rounded">
                                                <p className="mb-1"><strong>Bayi:</strong> {selectedApplication.dealerInfo.dealerName}</p>
                                                <p className="mb-0"><strong>Bayi ID:</strong> {selectedApplication.dealerInfo.dealerId}</p>
                                            </div>
                                        )}
                                    </Card.Body>
                                </Card>
                                {/* Belgeler Kartı */}
                                <Card className="mt-3">
                                    <Card.Header>
                                        <h6 className="mb-0">
                                            <FaFile className="me-2" />
                                            Belgeler
                                        </h6>
                                    </Card.Header>
                                    <Card.Body>
                                        {documentsLoading ? (
                                            <div className="text-center py-3">
                                                <Spinner animation="border" size="sm" />
                                                <span className="ms-2">Belgeler yükleniyor...</span>
                                            </div>
                                        ) : applicationDocuments.length > 0 ? (
                                            <div>
                                                {applicationDocuments.map((doc) => (
                                                    <div key={doc._id} className="d-flex justify-content-between align-items-center border-bottom py-2">
                                                        <div>
                                                            <small className="text-muted">{doc.categoryName || doc.category}</small>
                                                            <div>{doc.file?.originalName}</div>
                                                        </div>
                                                        <div>
                                                            <Button
                                                                variant="outline-primary"
                                                                size="sm"
                                                                onClick={() => handleDownload(doc)}
                                                            >
                                                                <FaDownload className="me-1" /> İndir
                                                            </Button>
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <p className="text-muted mb-0">Henüz belge yüklenmemiş</p>
                                        )}
                                    </Card.Body>
                                </Card>
                            </Col>
                        </Row>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowDetailModal(false)}>
                        Kapat
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default AdminApplicationsPage;